import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch } from "vue";
  import {
    calculateLotCenter,
    applyOffset,
    createLotNumberTransform,
  } from "@/helpers/lotNumber/lotCentroidCalculator";

  interface LotNumberMetadata {
    lotId: string;
    offsetX: number;
    offsetY: number;
    scale: number;
    rotation: number;
    displayed: boolean;
  }

  interface Props {
    isOpen: boolean;
    lotId: string;
    lotNumber: string;
    lot: {
      polygonPoints?: Array<{ x: number; y: number }>;
      boundingBox?: {
        x: number;
        y: number;
        width: number;
        height: number;
      };
    };
    metadata?: Partial<LotNumberMetadata>;
  }

  interface Emits {
    (e: "close"): void;
    (e: "save", metadata: LotNumberMetadata): void;
  }

  const previewViewBox = "0 0 300 250";
  const previewContainerSize = 300;
  const previewLotX = 50;
  const previewLotY = 50;
  const previewLotWidth = 200;
  const previewLotHeight = 150;

  
export default /*@__PURE__*/_defineComponent({
  __name: 'LotNumberEditor',
  props: {
    isOpen: { type: Boolean },
    lotId: {},
    lotNumber: {},
    lot: {},
    metadata: { default: () => ({
      offsetX: 0,
      offsetY: 0,
      scale: 1,
      rotation: 0,
      displayed: true,
    }) }
  },
  emits: ["close", "save"],
  setup(__props: any, { expose: __expose, emit: __emit }) {
  __expose();

  const props = __props;

  const emit = __emit;

  const saving = ref(false);
  const localMetadata = ref<LotNumberMetadata>({
    lotId: props.lotId,
    offsetX: props.metadata?.offsetX ?? 0,
    offsetY: props.metadata?.offsetY ?? 0,
    scale: props.metadata?.scale ?? 1,
    rotation: props.metadata?.rotation ?? 0,
    displayed: props.metadata?.displayed ?? true,
  });

  // Preview SVG configuration
  const previewCenterX = computed(() => previewLotX + previewLotWidth / 2);
  const previewCenterY = computed(() => previewLotY + previewLotHeight / 2);

  const previewFontSize = computed(() => {
    const baseSize = Math.min(previewLotWidth, previewLotHeight) / 8;

    return baseSize * localMetadata.value.scale;
  });

  const previewTransform = computed(() => {
    const offset = {
      x: localMetadata.value.offsetX / 100,
      y: localMetadata.value.offsetY / 100,
    };

    const center = {
      x: previewCenterX.value,
      y: previewCenterY.value,
    };

    const finalPos = applyOffset(
      center,
      offset.x * previewLotWidth,
      offset.y * previewLotHeight
    );

    const transforms = [
      `translate(${finalPos.x}, ${finalPos.y})`,
      localMetadata.value.scale !== 1
        ? `scale(${localMetadata.value.scale})`
        : "",
      localMetadata.value.rotation !== 0
        ? `rotate(${localMetadata.value.rotation})`
        : "",
      `translate(${-finalPos.x}, ${-finalPos.y})`,
    ].filter(Boolean);

    return transforms.join(" ");
  });

  watch(
    () => props.isOpen,
    (newVal) => {
      if (newVal && props.metadata) {
        localMetadata.value = {
          lotId: props.lotId,
          offsetX: props.metadata.offsetX ?? 0,
          offsetY: props.metadata.offsetY ?? 0,
          scale: props.metadata.scale ?? 1,
          rotation: props.metadata.rotation ?? 0,
          displayed: props.metadata.displayed ?? true,
        };
      }
    }
  );

  const handleClose = () => {
    emit("close");
  };

  const handleSave = async () => {
    saving.value = true;

    try {
      await new Promise((resolve) => setTimeout(resolve, 300)); // Simulate save delay
      emit("save", localMetadata.value);
    } finally {
      saving.value = false;
    }
  };

const __returned__ = { props, emit, saving, localMetadata, previewViewBox, previewContainerSize, previewLotX, previewLotY, previewLotWidth, previewLotHeight, previewCenterX, previewCenterY, previewFontSize, previewTransform, handleClose, handleSave, ref, computed, watch, get calculateLotCenter() { return calculateLotCenter }, get applyOffset() { return applyOffset }, get createLotNumberTransform() { return createLotNumberTransform } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})